import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a Singapore GST invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Singapore GST tax invoice must include: the words "Tax Invoice"; your business name and GST registration number (UEN format); the invoice date and a unique sequential invoice number; the customer name and address; a description of goods or services; the taxable amount, GST rate (9%), GST amount, and total amount payable in SGD.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the GST rate in Singapore?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Singapore\'s GST rate is 9% as of 1 January 2024 (increased from 8% in 2023 and 7% before that). GST is administered by the Inland Revenue Authority of Singapore (IRAS). Zero-rated supplies (0% GST) include most international services and exports.',
      },
    },
    {
      '@type': 'Question',
      name: 'When do I need to register for GST in Singapore?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You must register for GST if your annual taxable turnover exceeds SGD 1 million. Voluntary registration is also available for businesses below this threshold. Once registered, IRAS issues a GST registration number which must appear on all tax invoices.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a Singapore GST invoice for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Use the Invilic VAT invoice template for Singapore GST invoices. Enter your UEN/GST registration number in the VAT number field, set the rate to 9%, and your line items in SGD. The GST amount is calculated automatically. Download a professional PDF tax invoice free, no sign-up required.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do Singapore freelancers need to charge GST?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Only if you are GST-registered. Most freelancers and small businesses in Singapore are below the SGD 1 million threshold and do not need to register for GST. If you are not registered, issue a standard invoice without GST. If you are registered, you must charge 9% GST on all taxable supplies and issue proper tax invoices.',
      },
    },
  ],
};

export default function InvoiceGeneratorSingaporePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Singapore: Free GST Tax Invoice Maker | Invilic"
        description="Free GST invoice generator for Singapore businesses and freelancers. Create IRAS-compliant 9% GST tax invoices with UEN. Download professional PDF free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-singapore"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇸🇬 Singapore
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Singapore
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free invoice generator for Singapore businesses and freelancers. Create IRAS-compliant GST tax invoices with 9% GST auto-calculated, or standard invoices for non-GST-registered businesses. Download professional PDF invoices in SGD instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/create/vat"
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200"
            >
              Create GST Tax Invoice <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/create/freelancer"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200"
            >
              Create Standard Invoice <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* GST at a glance */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Singapore GST at a Glance</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { label: 'GST Rate', value: '9%', detail: 'Standard rate as of 1 January 2024 (IRAS)' },
              { label: 'Registration Threshold', value: 'SGD 1 million', detail: 'Mandatory GST registration above this annual taxable turnover' },
              { label: 'Zero-Rated Supplies', value: '0%', detail: 'Exports and most international services are zero-rated' },
            ].map(({ label, value, detail }) => (
              <div key={label} className="text-center p-5 rounded-xl border border-red-100 dark:border-red-800/30 bg-red-50 dark:bg-red-900/10">
                <div className="text-2xl font-extrabold text-red-700 dark:text-red-300 mb-1">{value}</div>
                <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{label}</div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Invoice types */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Which Invoice Do You Need?
          </h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              {
                icon: Percent,
                title: 'GST Tax Invoice',
                subtitle: 'For GST-registered businesses',
                features: ['UEN / GST reg number field', '9% GST auto-calculated', 'Net + GST + total breakdown', 'SGD billing', 'IRAS-compliant format'],
                to: '/create/vat',
                color: 'red',
                btnClass: 'bg-red-600 hover:bg-red-700',
              },
              {
                icon: FileText,
                title: 'Standard Invoice',
                subtitle: 'For non-GST-registered businesses',
                features: ['No GST required', 'SGD or USD billing', 'Custom line items', 'Logo upload', 'Payment terms'],
                to: '/create/freelancer',
                color: 'blue',
                btnClass: 'bg-blue-600 hover:bg-blue-700',
              },
              {
                icon: Globe,
                title: 'International Invoice',
                subtitle: 'For billing overseas clients',
                features: ['USD, EUR, GBP billing', 'SWIFT / IBAN fields', 'Zero-rated export format', 'Multi-currency support'],
                to: '/create/international',
                color: 'indigo',
                btnClass: 'bg-indigo-600 hover:bg-indigo-700',
              },
            ].map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={to}
                  className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}
                >
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* How to create */}
        <section className="mb-16 p-8 rounded-2xl border border-red-100 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">
            How to Create a Singapore GST Invoice with Invilic
          </h2>
          <div className="space-y-3">
            {[
              'Click "Create GST Tax Invoice" and select the VAT invoice type.',
              'Enter your UEN or GST registration number in the VAT Number field.',
              'Set the GST rate to 9% — the GST amount calculates automatically.',
              'Add your line items in SGD with descriptions and amounts.',
              'Download the PDF. It shows the taxable amount, 9% GST, and total payable.',
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{step}</span>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Singapore Invoice Generator: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Singapore Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">IRAS-compliant PDF tax invoice in under 2 minutes. No account required.</p>
          <Link
            to="/create/vat"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200 text-lg"
          >
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
